import React from 'react';

import { graphql } from 'gatsby';

import { SxProps, Theme, useTheme } from '@mui/material';

import Image from '../../../components/image';

export const ImageFileFragment = graphql`
  fragment StrapiImageFileFragment on STRAPI__MEDIA {
    fields {
      assetUrl
    }
    size
    width
    height
  }

  fragment StrapiImageFragment on STRAPI__COMPONENT_SHARED_RESPONSIVE_IMAGE {
    imageDesktop {
      ...StrapiImageFileFragment
    }
    imageMobile {
      ...StrapiImageFileFragment
    }
    imageAlternativeText
  }
`;

export type StrapiImageData = {
  fields: {
    assetUrl: string;
  };
  width: number;
  height: number;
};

export type StrapiResponsiveImageData = {
  imageDesktop: StrapiImageData | null;
  imageMobile: StrapiImageData | null;
  imageAlternativeText: string | null;
};

export const gatsbyImageDataFromStrapiImage = (
  image: Pick<StrapiResponsiveImageData, 'imageDesktop' | 'imageMobile'>,
  theme: Theme,
) => {
  if (!image.imageMobile) {
    return image.imageDesktop
      ? [
          {
            src: image.imageDesktop.fields.assetUrl,
            media: '',
            width: image.imageDesktop.width,
            height: image.imageDesktop.height,
          },
        ]
      : [];
  }

  return [
    ...(image.imageDesktop
      ? [
          {
            src: image.imageDesktop.fields.assetUrl,
            media: `(min-width: ${theme.breakpoints.values.sm}px)`,
            width: image.imageDesktop.width,
            height: image.imageDesktop.height,
          },
        ]
      : []),
    ...(image.imageMobile
      ? [
          {
            src: image.imageMobile.fields.assetUrl,
            media: `(max-width: ${theme.breakpoints.values.sm - 1}px)`,
            width: image.imageMobile.width,
            height: image.imageMobile.height,
          },
        ]
      : []),
  ];
};

type Props = {
  alt?: string;
  objectFit?: React.CSSProperties['objectFit'];
  intendedForSearchIndex?: boolean;
  style?: SxProps;
  className?: string;
  imageAspectRatioWide?: boolean;
} & (
  | {
      image?: StrapiImageData | null;
    }
  | {
      responsiveImage: StrapiResponsiveImageData;
    }
);

export const StrapiImage = React.memo(
  ({
    alt,
    objectFit,
    intendedForSearchIndex,
    style,
    className,
    imageAspectRatioWide,
    ...imageProps
  }: Props) => {
    const theme = useTheme();
    const restProps = {
      objectFit,
      intendedForSearchIndex,
      style,
      className,
      imageAspectRatioWide,
    };

    if ('responsiveImage' in imageProps) {
      return (
        <Image
          alt={alt ?? imageProps.responsiveImage.imageAlternativeText ?? ''}
          images={gatsbyImageDataFromStrapiImage(
            imageProps.responsiveImage,
            theme,
          )}
          {...restProps}
        />
      );
    } else {
      return (
        <Image
          alt={alt ?? ''}
          images={[
            {
              src: imageProps.image?.fields.assetUrl ?? '',
              media: `(min-width: 0px)`,
              width: imageProps.image?.width ?? 0,
              height: imageProps.image?.height ?? 0,
            },
          ]}
          {...restProps}
        />
      );
    }
  },
);
